import {
  Button,
  Alert,
  PhoneInputField,
  FormField,
  TextInput,
  ToggleInput,
} from "@a2z/ui";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { toE164OrNull } from "@/lib/phone/phone";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useCreateAdminMutation } from "../services/useCreateAdminMutation";
import { getRequiredFieldErrors } from "@/shared/components/forms/validation";
import { getPhoneInputLabels } from "@/shared/components/forms/phone-input-labels";

export function CreateForm({ locale }: { locale: string }) {
  const navigate = useNavigate();
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phone, setPhone] = useState("");
  const { mutateAsync: createAdmin } = useCreateAdminMutation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const requiredErrors = getRequiredFieldErrors(
      formData,
      ["name", "email", "password"],
      t(keys.errors.required),
    );
    if (phone.trim().length === 0) {
      requiredErrors.phone = t(keys.errors.required);
    }
    const roles = formData.getAll("roles").map((value) => String(value));
    if (roles.length === 0) {
      requiredErrors.roles = t(keys.errors.required);
    }

    if (Object.keys(requiredErrors).length > 0) {
      setFieldErrors(requiredErrors);
      setIsSubmitting(false);
      return;
    }
    const normalizedPhone = toE164OrNull(phone);
    if (!normalizedPhone) {
      setFieldErrors({ phone: t(keys.buyer.trackingValidationPhone) });
      setIsSubmitting(false);
      return;
    }

    try {
      await createAdmin({
        name: String(formData.get("name") ?? ""),
        email: String(formData.get("email") ?? ""),
        phone: normalizedPhone,
        password: String(formData.get("password") ?? ""),
        roles: roles.length > 0 ? roles : undefined,
      });
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      setIsSubmitting(false);
      return;
    }

    navigate(`/${locale}/admin/admins`, { replace: true });
  };

  return (
    <form
      className="mx-auto flex w-full max-w-[640px] flex-col gap-4"
      onSubmit={handleSubmit}
    >
      {error ? (
        <Alert message={error} helperText={t(keys.errors.recovery)} />
      ) : null}
      <FormField
        label={t(keys.common.name)}
        name="name"
        required
        error={fieldErrors.name}
      >
        <TextInput
          name="name"
          placeholder={t(keys.placeholders.name)}
          required
          hasError={Boolean(fieldErrors.name)}
        />
      </FormField>
      <div className="grid gap-4 lg:grid-cols-2">
        <FormField
          label={t(keys.common.email)}
          name="email"
          required
          error={fieldErrors.email}
        >
          <TextInput
            name="email"
            type="email"
            placeholder={t(keys.placeholders.email)}
            required
            hasError={Boolean(fieldErrors.email)}
          />
        </FormField>
        <FormField
          label={t(keys.common.phone)}
          name="phone"
          required
          error={fieldErrors.phone}
        >
          <PhoneInputField
            name="phone"
            value={phone}
            onChange={setPhone}
            locale={locale}
            labels={getPhoneInputLabels(t)}
            required
            hasError={Boolean(fieldErrors.phone)}
          />
        </FormField>
      </div>
      <FormField
        label={t(keys.common.password)}
        name="password"
        required
        error={fieldErrors.password}
      >
        <TextInput
          name="password"
          type="password"
          placeholder={t(keys.placeholders.password)}
          required
          hasError={Boolean(fieldErrors.password)}
        />
      </FormField>
      <FormField
        label={t(keys.admin.rolesLabel)}
        name="roles"
        required
        error={fieldErrors.roles}
        helperText={t(keys.admin.rolesHelper)}
      >
        <div className="grid gap-2 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-sm">
          {[
            ADMIN_ROLES.ADMIN,
            ADMIN_ROLES.SUPER_ADMIN,
            ADMIN_ROLES.CUSTOMER_SERVICE,
            ADMIN_ROLES.SALES,
          ].map((role) => (
            <label key={role} className="flex items-center gap-3">
              <ToggleInput
                name="roles"
                defaultChecked={role === ADMIN_ROLES.ADMIN}
                value={role}
              />
              <span className="text-[color:var(--text-secondary)]">
                {t(
                  role === ADMIN_ROLES.ADMIN
                    ? keys.admin.roleAdmin
                    : role === ADMIN_ROLES.SUPER_ADMIN
                      ? keys.admin.roleSuperAdmin
                      : role === ADMIN_ROLES.CUSTOMER_SERVICE
                        ? keys.admin.roleCustomerService
                        : keys.admin.roleSales,
                )}
              </span>
            </label>
          ))}
        </div>
      </FormField>
      <Button
        type="submit"
        disabled={isSubmitting}
        variant="primary"
        className="h-11 w-full rounded-lg px-4 text-sm font-semibold transition disabled:opacity-60"
      >
        {t(keys.common.create)}
      </Button>
    </form>
  );
}
